import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Bot,
  Monitor,
  Users,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ShieldOff,
  Search,
  Zap,
  LayoutGrid,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  HardDrive,
  Network,
  Terminal,
  ClipboardList,
  Globe2,
  Loader2,
  RefreshCw,
} from "lucide-react";
import AgenticActivity from "./AgenticActivity";
import { formatAgentDisplayName } from "./agentDisplayName";
import useUserStore from "../../../services/userStore";
import {
  getAgenticActivityOverview,
  getAgenticActivityTimeline,
  getAgentInventory,
  getAgentEndpoints,
  getTopEndpoints,
  getTopUsers,
  updateAgentStatus,
  buildDashboardRangeParams,
  AgentInventoryItem,
  AgentEndpointDetail,
  AgenticActivityOverview,
  AgenticActivityTimelinePoint,
  TopEndpointItem,
  TopUserItem,
  AgentGovernanceStatus,
  DashboardDuration,
} from "./Api/globalAgenticActivityApi";
import { myLocalStorage } from "../../StorageHelper";

type AgentCap = "fs" | "net" | "shell" | "clipboard" | "browser";
type GovernanceFilter = "all" | "APPROVED" | "BLOCKED";

const CAPABILITY_MAP: Record<string, AgentCap> = {
  FILE_SYSTEM: "fs",
  NETWORK: "net",
  SHELL: "shell",
  CLIPBOARD: "clipboard",
  BROWSER: "browser",
};

const PALETTE = [
  { badgeCls: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border border-orange-200 dark:border-orange-700", cardRing: "border-orange-200 dark:border-orange-800", iconBg: "bg-orange-100 dark:bg-orange-900/50", iconText: "text-orange-600 dark:text-orange-400", dotCls: "bg-orange-500" },
  { badgeCls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300 border border-sky-200 dark:border-sky-700", cardRing: "border-sky-200 dark:border-sky-800", iconBg: "bg-sky-100 dark:bg-sky-900/50", iconText: "text-sky-600 dark:text-sky-400", dotCls: "bg-sky-500" },
  { badgeCls: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-violet-200 dark:border-violet-700", cardRing: "border-violet-200 dark:border-violet-800", iconBg: "bg-violet-100 dark:bg-violet-900/50", iconText: "text-violet-600 dark:text-violet-400", dotCls: "bg-violet-500" },
  { badgeCls: "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300 border border-teal-200 dark:border-teal-700", cardRing: "border-teal-200 dark:border-teal-800", iconBg: "bg-teal-100 dark:bg-teal-900/50", iconText: "text-teal-600 dark:text-teal-400", dotCls: "bg-teal-500" },
  { badgeCls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700", cardRing: "border-blue-200 dark:border-blue-800", iconBg: "bg-blue-100 dark:bg-blue-900/50", iconText: "text-blue-600 dark:text-blue-400", dotCls: "bg-blue-500" },
  { badgeCls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700", cardRing: "border-emerald-200 dark:border-emerald-800", iconBg: "bg-emerald-100 dark:bg-emerald-900/50", iconText: "text-emerald-600 dark:text-emerald-400", dotCls: "bg-emerald-500" },
  { badgeCls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-700", cardRing: "border-rose-200 dark:border-rose-800", iconBg: "bg-rose-100 dark:bg-rose-900/50", iconText: "text-rose-600 dark:text-rose-400", dotCls: "bg-rose-500" },
];

function hashAgentKey(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash + key.charCodeAt(i) * (i + 1)) % PALETTE.length;
  }
  return hash;
}

function getAgentStyle(agentKey: string) {
  return PALETTE[hashAgentKey(agentKey)] ?? PALETTE[0];
}

function mapCapabilities(caps: string[]): AgentCap[] {
  return caps
    .map(c => CAPABILITY_MAP[c.toUpperCase()])
    .filter(Boolean) as AgentCap[];
}

function relTime(ts: string | null | undefined): string {
  if (!ts) return "—";
  const diff = Date.now() - new Date(ts).getTime();
  if (Number.isNaN(diff)) return "—";
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "< 1h ago";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function formatChartDate(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function AgentPill({ agentKey, displayName }: { agentKey: string; displayName?: string }) {
  const style = getAgentStyle(agentKey);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${style.badgeCls}`}>
      <Bot className="w-3 h-3" />{displayName ?? formatAgentDisplayName(agentKey)}
    </span>
  );
}

function StatusBadge({ status }: { status: AgentGovernanceStatus }) {
  const cfg = status === "APPROVED"
    ? { label: "Approved", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700", Icon: CheckCircle2 }
    : status === "BLOCKED"
      ? { label: "Blocked", cls: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 border border-rose-200 dark:border-rose-700", Icon: ShieldOff }
      : { label: "Pending", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-200 dark:border-amber-700", Icon: AlertTriangle };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
}

const CAP_CONFIG: Record<AgentCap, {
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  cls: string;
}> = {
  fs: { label: "File System", Icon: HardDrive, cls: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800" },
  net: { label: "Network", Icon: Network, cls: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800" },
  shell: { label: "Shell", Icon: Terminal, cls: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800" },
  clipboard: { label: "Clipboard", Icon: ClipboardList, cls: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700" },
  browser: { label: "Browser", Icon: Globe2, cls: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-800" },
};

function CapabilityTags({ caps, size = "md" }: { caps: AgentCap[]; size?: "sm" | "md" }) {
  if (!caps.length) {
    return <span className="text-[10px] text-muted-foreground">No capabilities detected yet</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {caps.map(c => {
        const { label, Icon, cls } = CAP_CONFIG[c];
        return (
          <span key={c} title={label} className={`inline-flex items-center gap-1 rounded border font-medium ${cls} ${size === "sm" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"}`}>
            <Icon className={size === "sm" ? "w-2.5 h-2.5" : "w-3 h-3"} />{label}
          </span>
        );
      })}
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
      <Loader2 className="w-6 h-6 animate-spin" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <AlertTriangle className="w-6 h-6 text-rose-500" />
      <p className="text-sm text-muted-foreground text-center max-w-md">{message}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Retry
      </Button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
      <Bot className="w-6 h-6 opacity-50" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, iconCls = "text-muted-foreground" }: {
  icon: React.ComponentType<{ className?: string }>;
  label: string; value: string | number; sub?: string; iconCls?: string;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-1">{label}</p>
            <p className="text-3xl font-bold text-foreground leading-none">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${iconCls}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function durationLabel(duration: DashboardDuration, customStart?: string, customEnd?: string): string {
  if (duration === "custom" && customStart && customEnd) {
    return `${customStart} – ${customEnd}`;
  }
  if (duration === "7") return "Last 7 Days";
  if (duration === "30") return "Last 30 Days";
  if (duration === "90") return "Last 90 Days";
  return "Custom range";
}

function ActivityTimeline({ data, loading, label }: { data: AgenticActivityTimelinePoint[]; loading: boolean; label: string }) {
  const chartData = useMemo(
    () => data.map(point => ({ t: formatChartDate(point.date), events: point.events })),
    [data]
  );

  return (
    <Card>
      <CardContent className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <p className="text-xs font-semibold text-foreground">Agent Activity — {label}</p>
          <p className="text-[10px] text-muted-foreground">Total events across all endpoints</p>
        </div>
        {loading ? (
          <div className="h-[110px] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : chartData.length === 0 ? (
          <EmptyState message="No activity recorded in the selected period" />
        ) : (
          <ResponsiveContainer width="100%" height={110}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="t" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))", color: "hsl(var(--foreground))", padding: "4px 10px" }}
                formatter={(v: number) => [v, "events"]}
              />
              <Area type="monotone" dataKey="events" stroke="#6366f1" strokeWidth={2} fill="url(#areaGrad)" dot={false} activeDot={{ r: 3, fill: "#6366f1" }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function AgentCard({ agent, maxEvents, onStatusChange, onClick, isSelected, statusUpdating }: {
  agent: AgentInventoryItem;
  maxEvents: number;
  onStatusChange: (agentId: number, status: AgentGovernanceStatus) => void;
  onClick: () => void;
  isSelected: boolean;
  statusUpdating: boolean;
}) {
  const style = getAgentStyle(agent.agentKey);
  const caps = mapCapabilities(agent.capabilities);
  const share = maxEvents > 0 ? Math.round((agent.events / maxEvents) * 100) : 0;

  return (
    <Card onClick={onClick} className={`border flex flex-col cursor-pointer transition-shadow ${style.cardRing} ${isSelected ? "ring-2 ring-foreground/30 shadow-md" : "hover:shadow-sm"}`}>
      <CardContent className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${style.iconBg}`}>
              <Bot className={`w-5 h-5 ${style.iconText}`} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-foreground text-sm">{formatAgentDisplayName(agent.agentKey)}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">First seen {relTime(agent.firstSeen)}</p>
            </div>
          </div>
          {/**
           * <StatusBadge status={agent.status} />
           */}

        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md border border-border px-2 py-2"><p className="text-lg font-bold leading-none">{agent.events}</p><p className="text-[10px] text-muted-foreground mt-0.5">events</p></div>
          <div className="rounded-md border border-border px-2 py-2"><p className="text-lg font-bold leading-none">{agent.endpoints}</p><p className="text-[10px] text-muted-foreground mt-0.5">endpoints</p></div>
          <div className="rounded-md border border-border px-2 py-2"><p className="text-lg font-bold leading-none">{agent.users}</p><p className="text-[10px] text-muted-foreground mt-0.5">users</p></div>
        </div>

        <div>
          <p className="text-[10px] text-muted-foreground mb-1.5 font-medium uppercase tracking-wider">System Access</p>
          <CapabilityTags caps={caps} />
        </div>

        <div>
          <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
            <span>Activity share</span>
            <span>{agent.activityShare || share}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-border/40 overflow-hidden">
            <div className={`h-full rounded-full ${style.dotCls}`} style={{ width: `${agent.activityShare || share}%` }} />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 flex-wrap pt-1 border-t border-border">
          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />Last seen {relTime(agent.lastSeen)}
          </span>
          {/**
           * <div className="flex gap-1" onClick={e => e.stopPropagation()}>
            {agent.status !== "APPROVED" && (
              <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "APPROVED")}>
                <CheckCircle2 className="w-3 h-3 mr-1" />Approve
              </Button>
            )}
            {agent.status !== "BLOCKED" && (
              <Button size="sm" variant="outline" className="text-[10px] h-6 px-2 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "BLOCKED")}>
                <ShieldOff className="w-3 h-3 mr-1" />Block
              </Button>
            )}
          </div>
           */}

        </div>
      </CardContent>
    </Card>
  );
}

const AGENTS_PER_PAGE = 9;
const ENDPOINT_PAGE_SIZE = 100;

function AgentDrillDown({ agent, tenantId, onStatusChange, onClose, statusUpdating }: {
  agent: AgentInventoryItem;
  tenantId: string;
  onStatusChange: (agentId: number, status: AgentGovernanceStatus) => void;
  onClose: () => void;
  statusUpdating: boolean;
}) {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [endpoints, setEndpoints] = useState<AgentEndpointDetail[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  const loadEndpoints = useCallback(async (append: boolean, startOffset: number, query: string) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await getAgentEndpoints(tenantId, agent.agentId, {
        offset: startOffset,
        limit: ENDPOINT_PAGE_SIZE,
        search: query || undefined,
      });
      const items = response?.items ?? [];
      setEndpoints(prev => append ? [...prev, ...items] : items);
      setTotalCount(response?.total ?? items.length);
      setHasMore(response?.hasMore ?? false);
    } catch {
      setError("Failed to load endpoints.");
      if (!append) setEndpoints([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, [tenantId, agent.agentId]);

  useEffect(() => {
    loadEndpoints(false, 0, searchQuery);
  }, [loadEndpoints, searchQuery]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (value.trim() === "" && searchQuery.trim() !== "") {
      setSearchQuery("");
    }
  };

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore || loadingRef.current) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      loadEndpoints(true, endpoints.length, searchQuery);
    }
  }, [endpoints.length, hasMore, loadingMore, loadEndpoints, searchQuery]);

  const style = getAgentStyle(agent.agentKey);
  const caps = mapCapabilities(agent.capabilities);

  return (
    <div className="flex flex-col h-full border-l border-border bg-white dark:bg-background">
      <div className="px-5 py-4 border-b border-border bg-white dark:bg-background">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border border-border">
              <Bot className={`w-4 h-4 ${style.iconText}`} />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">{formatAgentDisplayName(agent.agentKey)}</p>
              <div className="mt-0.5"><StatusBadge status={agent.status} /></div>
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="mt-3"><CapabilityTags caps={caps} size="sm" /></div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {[
            { label: "Events", value: agent.events },
            { label: "Endpoints", value: agent.endpoints },
            { label: "Users", value: agent.users },
          ].map(s => (
            <div key={s.label} className="rounded-md border border-border px-2 py-2 text-center">
              <p className="text-base font-bold leading-none">{s.value}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          {agent.status !== "APPROVED" && (
            <Button size="sm" variant="outline" className="flex-1 text-xs h-7" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "APPROVED")}>
              <CheckCircle2 className="w-3 h-3 mr-1.5 text-emerald-600" />Approve
            </Button>
          )}
          {agent.status !== "BLOCKED" && (
            <Button size="sm" variant="outline" className="flex-1 text-xs h-7 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-800" disabled={statusUpdating} onClick={() => onStatusChange(agent.agentId, "BLOCKED")}>
              <ShieldOff className="w-3 h-3 mr-1.5" />Block Agent
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-foreground">Endpoints using this agent</p>
            <span className="text-[10px] text-muted-foreground">{totalCount} found</span>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-8 text-xs"
                placeholder="Search by name, OS, user…"
                value={searchInput}
                onChange={e => handleSearchInputChange(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
              />
            </div>
            <Button size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={handleSearch}>
              <Search className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto divide-y divide-border" onScroll={handleScroll}>
          {loading ? <LoadingState label="Loading endpoints…" /> :
            error ? <ErrorState message={error} onRetry={() => loadEndpoints(false, 0, searchQuery)} /> :
              endpoints.length === 0 ? <EmptyState message="No endpoints match your search" /> :
                <>
                  {endpoints.map(ep => (
                    <div key={`${ep.computerId}-${ep.lastSeen ?? ""}`} className="px-5 py-3.5 hover-elevate">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-md border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Monitor className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm leading-tight">{ep.computerName}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{ep.osName}</p>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {ep.otherAgents.map(a => <AgentPill key={a} agentKey={a} />)}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {ep.users.map(u => (
                                <span key={u} className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                                  <User className="w-2.5 h-2.5" />{u}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold">{ep.events}</p>
                          <p className="text-[10px] text-muted-foreground">events</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{relTime(ep.lastSeen)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {loadingMore && (
                    <div className="flex items-center justify-center py-4 text-muted-foreground gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-xs">Loading more…</span>
                    </div>
                  )}
                </>
          }
        </div>
      </div>
    </div>
  );
}

function EndpointsTable({ items, search, loading }: { items: TopEndpointItem[]; search: string; loading: boolean }) {
  const filtered = useMemo(
    () => items.filter(e => e.computerName.toLowerCase().includes(search.toLowerCase())),
    [items, search]
  );

  return (
    <Card>
      <CardHeader className="pb-2 pt-5 px-5">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Monitor className="w-4 h-4 text-muted-foreground" />Top Endpoints
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {loading ? <LoadingState label="Loading endpoints…" /> :
          filtered.length === 0 ? <EmptyState message="No endpoint activity found" /> :
            <div className="space-y-0 divide-y divide-border">
              {filtered.slice(0, 10).map(ep => (
                <div key={ep.computerId} className="flex items-start justify-between gap-3 py-3 hover-elevate rounded-md px-1">
                  <div className="flex items-start gap-2.5 min-w-0">
                    <div className="w-8 h-8 rounded-md border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Monitor className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{ep.computerName}</span>
                        {ep.osName && <span className="text-[10px] text-muted-foreground">{ep.osName}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {ep.agents.map(a => <AgentPill key={a} agentKey={a} />)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold">{ep.events}</p>
                    <p className="text-[10px] text-muted-foreground">events</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{relTime(ep.lastSeen)}</p>
                  </div>
                </div>
              ))}
            </div>}
      </CardContent>
    </Card>
  );
}

function UsersTable({ items, search, loading, blockedAgentKeys }: {
  items: TopUserItem[]; search: string; loading: boolean; blockedAgentKeys: Set<string>;
}) {
  const filtered = useMemo(
    () => items.filter(u =>
      u.username.toLowerCase().includes(search.toLowerCase())
    ),
    [items, search]
  );

  return (
    <Card>
      <CardHeader className="pb-2 pt-5 px-5">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />Top Users
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        {loading ? <LoadingState label="Loading users…" /> :
          filtered.length === 0 ? <EmptyState message="No user activity found" /> :
            <div className="space-y-0 divide-y divide-border">
              {filtered.slice(0, 10).map(u => {
                const flagged = u.agents.some(a => blockedAgentKeys.has(a));
                return (
                  <div key={u.userId} className={`flex items-start justify-between gap-3 py-3 hover-elevate rounded-md px-1 ${flagged ? "bg-rose-50/30 dark:bg-rose-950/10" : ""}`}>
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full border border-border flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{u.username}</span>
                          {flagged && <AlertTriangle className="w-3 h-3 text-rose-500" />}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {u.agents.map(a => <AgentPill key={a} agentKey={a} />)}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {u.endpoints.map(ep => (
                            <span key={ep} className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              <Monitor className="w-2.5 h-2.5" />{ep}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold">{u.events}</p>
                      <p className="text-[10px] text-muted-foreground">events</p>
                    </div>
                  </div>
                );
              })}
            </div>}
      </CardContent>
    </Card>
  );
}

function OverviewTab({ tenantId, refreshKey, onRefresh, onOverviewChange }: {
  tenantId: string;
  refreshKey: number;
  onRefresh: () => void;
  onOverviewChange?: (overview: AgenticActivityOverview | null) => void;
}) {
  const [tableSearchInput, setTableSearchInput] = useState("");
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [agentFilter, setAgentFilter] = useState<GovernanceFilter>("all");
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [agentPage, setAgentPage] = useState(1);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [duration, setDuration] = useState<DashboardDuration>("7");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [appliedCustomStart, setAppliedCustomStart] = useState("");
  const [appliedCustomEnd, setAppliedCustomEnd] = useState("");

  const [agentNameInput, setAgentNameInput] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [agentsLoading, setAgentsLoading] = useState(false);

  const [overview, setOverview] = useState<AgenticActivityOverview | null>(null);
  const [timeline, setTimeline] = useState<AgenticActivityTimelinePoint[]>([]);
  const [agents, setAgents] = useState<AgentInventoryItem[]>([]);
  const [topEndpoints, setTopEndpoints] = useState<TopEndpointItem[]>([]);
  const [topUsers, setTopUsers] = useState<TopUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rangeParams = useMemo(() => {
    if (duration === "custom") {
      return buildDashboardRangeParams(duration, appliedCustomStart, appliedCustomEnd);
    }
    return buildDashboardRangeParams(duration);
  }, [duration, appliedCustomStart, appliedCustomEnd]);

  const timelineLabel = useMemo(
    () => durationLabel(duration, appliedCustomStart || customStartDate, appliedCustomEnd || customEndDate),
    [duration, appliedCustomStart, appliedCustomEnd, customStartDate, customEndDate]
  );

  const isPeriodFilterLoading = loading || agentsLoading;

  useEffect(() => {
    onOverviewChange?.(overview);
  }, [overview, onOverviewChange]);

  const loadAgents = useCallback(async () => {
    if (!tenantId) return;
    if (duration === "custom" && (!appliedCustomStart || !appliedCustomEnd)) return;

    setAgentsLoading(true);
    try {
      const agentsData = await getAgentInventory(tenantId, {
        ...rangeParams,
        status: agentFilter === "all" ? undefined : agentFilter,
        search: agentSearchQuery || undefined,
      });
      setAgents(agentsData ?? []);
    } catch {
      setError("Failed to load agent inventory.");
    } finally {
      setAgentsLoading(false);
    }
  }, [tenantId, rangeParams, agentFilter, agentSearchQuery, refreshKey, duration, appliedCustomStart, appliedCustomEnd]);

  const loadDashboard = useCallback(async () => {
    if (!tenantId) return;
    if (duration === "custom" && (!appliedCustomStart || !appliedCustomEnd)) return;

    setLoading(true);
    setError(null);
    try {
      const [overviewData, timelineData, endpointsData, usersData] = await Promise.all([
        getAgenticActivityOverview(tenantId, rangeParams),
        getAgenticActivityTimeline(tenantId, rangeParams),
        getTopEndpoints(tenantId, rangeParams),
        getTopUsers(tenantId, rangeParams),
      ]);
      setOverview(overviewData ?? null);
      setTimeline(timelineData ?? []);
      setTopEndpoints(endpointsData ?? []);
      setTopUsers(usersData ?? []);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load agentic activity dashboard.";
      setError(typeof message === "string" ? message : "Failed to load agentic activity dashboard.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, rangeParams, duration, appliedCustomStart, appliedCustomEnd, refreshKey]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { loadAgents(); }, [loadAgents]);

  const handleAgentSearch = () => {
    setAgentSearchQuery(agentNameInput.trim());
    setAgentPage(1);
    setSelectedAgentId(null);
  };

  const handleAgentSearchInputChange = (value: string) => {
    setAgentNameInput(value);
    if (value.trim() === "" && agentSearchQuery.trim() !== "") {
      setAgentSearchQuery("");
      setAgentPage(1);
      setSelectedAgentId(null);
    }
  };

  const handleTableSearch = () => {
    setTableSearchQuery(tableSearchInput.trim());
  };

  const handleTableSearchInputChange = (value: string) => {
    setTableSearchInput(value);
    if (value.trim() === "" && tableSearchQuery.trim() !== "") {
      setTableSearchQuery("");
    }
  };

  const handleDurationChange = (value: DashboardDuration) => {
    setDuration(value);
    setAgentPage(1);
    setSelectedAgentId(null);
    if (value !== "custom") {
      setAppliedCustomStart("");
      setAppliedCustomEnd("");
    }
  };

  const handleApplyCustomRange = () => {
    if (!customStartDate || !customEndDate) return;
    setAppliedCustomStart(customStartDate);
    setAppliedCustomEnd(customEndDate);
    setAgentPage(1);
    setSelectedAgentId(null);
  };

  const handleStatusChange = async (agentId: number, status: AgentGovernanceStatus) => {
    setStatusUpdating(true);
    try {
      const updated = await updateAgentStatus(tenantId, agentId, status);
      if (updated) {
        setAgents(prev => prev.map(a => a.agentId === agentId ? updated : a));
        const overviewData = await getAgenticActivityOverview(tenantId, rangeParams);
        setOverview(overviewData ?? null);
      }
    } catch {
      setError("Failed to update agent status.");
    } finally {
      setStatusUpdating(false);
    }
  };

  const filteredAgents = agents;
  const maxEvents = useMemo(() => Math.max(0, ...filteredAgents.map(a => a.events)), [filteredAgents]);
  const totalAgentPages = Math.max(1, Math.ceil(filteredAgents.length / AGENTS_PER_PAGE));
  const pagedAgents = filteredAgents.slice((agentPage - 1) * AGENTS_PER_PAGE, agentPage * AGENTS_PER_PAGE);
  const selectedAgent = agents.find(a => a.agentId === selectedAgentId) ?? null;
  const blockedAgentKeys = useMemo(
    () => new Set(agents.filter(a => a.status === "BLOCKED").map(a => a.agentKey)),
    [agents]
  );

  if (!tenantId) {
    return <EmptyState message="Select a tenant to view agentic activity" />;
  }

  if (loading && !overview) {
    return <LoadingState label="Loading dashboard…" />;
  }

  if (error && !overview) {
    return <ErrorState message={error} onRetry={loadDashboard} />;
  }

  return (
    <div className="flex gap-0 h-full">
      <div className={`flex-1 min-w-0 overflow-y-auto space-y-6 transition-all ${selectedAgent ? "pr-0" : ""}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground">Dashboard period</p>
          <div className={`flex items-center gap-2 flex-wrap relative ${isPeriodFilterLoading ? "opacity-70 pointer-events-none" : ""}`}>
            {isPeriodFilterLoading && (
              <div className="absolute -top-1 right-0 flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 z-10 pointer-events-none">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Applying filters…</span>
              </div>
            )}
            <Select value={duration} onValueChange={v => handleDurationChange(v as DashboardDuration)} disabled={isPeriodFilterLoading}>
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="custom">Custom date</SelectItem>
              </SelectContent>
            </Select>
            {duration === "custom" && (
              <>
                <Input type="date" className="h-8 w-36 text-xs" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} disabled={isPeriodFilterLoading} />
                <span className="text-xs text-muted-foreground">to</span>
                <Input type="date" className="h-8 w-36 text-xs" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} disabled={isPeriodFilterLoading} />
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleApplyCustomRange} disabled={!customStartDate || !customEndDate || isPeriodFilterLoading}>
                  Apply
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onRefresh} disabled={!tenantId || isPeriodFilterLoading}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isPeriodFilterLoading ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        </div>

        <ActivityTimeline data={timeline} loading={loading} label={timelineLabel} />

        <div className={`grid grid-cols-2 gap-3 sm:grid-cols-4 relative ${loading ? "opacity-60" : ""}`}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          )}
          <StatCard icon={Zap} label="Total Events" value={overview?.totalEvents ?? 0} sub="across all endpoints" iconCls="text-violet-600 dark:text-violet-400" />
          <StatCard icon={Bot} label="AI Agents Detected" value={overview?.totalAgentsDetected ?? 0} sub={`${overview?.blockedAgents ?? 0} blocked`} iconCls="text-orange-600 dark:text-orange-400" />
          <StatCard icon={Monitor} label="Endpoints w/ AI" value={overview?.totalEndpointsWithAi ?? 0} sub="running AI agent processes" iconCls="text-sky-600 dark:text-sky-400" />
          <StatCard icon={Users} label="Active Users" value={overview?.activeUsers ?? 0} sub="interacting with AI agents" iconCls="text-teal-600 dark:text-teal-400" />
        </div>

        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50/50 dark:bg-rose-950/20 px-4 py-2 text-xs text-rose-700 dark:text-rose-300 flex items-center justify-between gap-3">
            <span>{error}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={loadDashboard}>Retry</Button>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-foreground">AI Agent Inventory</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Click any agent to see which endpoints it is active on</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/**
               * 
               
              <div className="flex gap-1">
                {(["all", "APPROVED", "BLOCKED"] as const).map(f => (
                  <button
                    key={f}
                    onClick={() => { setAgentFilter(f); setAgentPage(1); setSelectedAgentId(null); }}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded-md border transition-colors ${agentFilter === f ? "bg-foreground text-background border-foreground" : "bg-background text-muted-foreground border-border hover-elevate"}`}
                  >
                    {f === "all" ? "All" : f === "APPROVED" ? "Approved" : "Blocked"}
                  </button>
                ))}
              </div>
              */}
              <div className="relative w-44 sm:w-52">
                <Input
                  className="pl-3 h-8 text-xs pr-2"
                  placeholder="Search by agent name…"
                  value={agentNameInput}
                  onChange={e => handleAgentSearchInputChange(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleAgentSearch(); }}
                  disabled={agentsLoading}
                />
              </div>
              <Button size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={handleAgentSearch} disabled={agentsLoading}>
                {agentsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
              </Button>
              {totalAgentPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className="h-6 w-6" disabled={agentPage === 1} onClick={() => setAgentPage(p => p - 1)}><ChevronLeft className="w-3 h-3" /></Button>
                  <span className="text-[11px] text-muted-foreground px-1">{agentPage} / {totalAgentPages}</span>
                  <Button size="icon" variant="outline" className="h-6 w-6" disabled={agentPage === totalAgentPages} onClick={() => setAgentPage(p => p + 1)}><ChevronRight className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          </div>

          {agentsLoading ? (
            <LoadingState label="Loading agents…" />
          ) : pagedAgents.length === 0 ? (
            <EmptyState message="No agents detected yet" />
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {pagedAgents.map(agent => (
                <AgentCard
                  key={agent.agentId}
                  agent={agent}
                  maxEvents={maxEvents}
                  onStatusChange={handleStatusChange}
                  onClick={() => setSelectedAgentId(selectedAgentId === agent.agentId ? null : agent.agentId)}
                  isSelected={selectedAgentId === agent.agentId}
                  statusUpdating={statusUpdating}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Endpoints &amp; Users</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Ranked by event volume</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-56">
                <Input
                  className="pl-3 h-8 text-xs"
                  placeholder="Filter endpoints or users…"
                  value={tableSearchInput}
                  onChange={e => handleTableSearchInputChange(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleTableSearch(); }}
                />
              </div>
              <Button size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={handleTableSearch}>
                <Search className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className={`grid grid-cols-1 gap-4 lg:grid-cols-2 relative ${loading ? "opacity-60" : ""}`}>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600 dark:text-blue-400" />
              </div>
            )}
            <EndpointsTable items={topEndpoints} search={tableSearchQuery} loading={loading} />
            <UsersTable items={topUsers} search={tableSearchQuery} loading={loading} blockedAgentKeys={blockedAgentKeys} />
          </div>
        </div>
      </div>

      {selectedAgent && (
        <div className="w-80 flex-shrink-0 border-l border-border overflow-hidden flex flex-col ml-6 rounded-md">
          <AgentDrillDown
            agent={selectedAgent}
            tenantId={tenantId}
            onStatusChange={handleStatusChange}
            onClose={() => setSelectedAgentId(null)}
            statusUpdating={statusUpdating}
          />
        </div>
      )}
    </div>
  );
}

function resolveActiveTenantId(): string {
  const storeTenant = useUserStore.getState().tenantName;
  const latestTenant = myLocalStorage.getItem("latestTenant");
  const storageTenant =
    typeof latestTenant === "string"
      ? latestTenant
      : latestTenant?.tenantName;
  const sessionTenant = useUserStore.getState().lastSession?.latestTenant;
  return (storeTenant || storageTenant || sessionTenant || "").trim();
}

export default function GlobalAgenticActivity() {
  const storeTenantName = useUserStore(state => state.tenantName);
  const [tenantId, setTenantId] = useState(resolveActiveTenantId);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");
  const [overviewSummary, setOverviewSummary] = useState<AgenticActivityOverview | null>(null);

  const handleOverviewChange = useCallback((overview: AgenticActivityOverview | null) => {
    setOverviewSummary(overview);
  }, []);

  useEffect(() => {
    setTenantId(resolveActiveTenantId());
  }, [storeTenantName]);

  const handleRefresh = () => {
    setTenantId(resolveActiveTenantId());
    setRefreshKey(k => k + 1);
  };

  useEffect(() => {
    if (!tenantId) {
      setOverviewSummary(null);
      return;
    }
    getAgenticActivityOverview(tenantId, buildDashboardRangeParams("7"))
      .then(data => setOverviewSummary(data ?? null))
      .catch(() => setOverviewSummary(null));
  }, [tenantId, refreshKey]);

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center">
              <Bot className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Agentic Activity</h1>
              <p className="text-xs text-muted-foreground">Enterprise-wide AI agent visibility &amp; governance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground border border-border rounded-md px-3 py-1.5">
              <Monitor className="w-3.5 h-3.5" />
              <span>
                {overviewSummary
                  ? `${overviewSummary.totalEndpointsWithAi} endpoints · ${overviewSummary.totalEvents.toLocaleString()} events · live`
                  : "Loading summary…"}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <div className="px-6 border-b border-border bg-background">
            <TabsList className="h-9 bg-transparent gap-0 p-0 rounded-none">
              <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground px-4 h-9 text-xs font-medium">
                <LayoutGrid className="w-3.5 h-3.5 mr-1.5" />Global Overview
              </TabsTrigger>
              <TabsTrigger value="activity" className="rounded-none border-b-2 border-transparent data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground px-4 h-9 text-xs font-medium">
                <Activity className="w-3.5 h-3.5 mr-1.5" />Global Activity
              </TabsTrigger>
            </TabsList>
          </div>

          {activeTab === "overview" && (
            <div className="flex-1 min-h-0 overflow-hidden mt-0 p-6 flex flex-col">
              <OverviewTab
                tenantId={tenantId}
                refreshKey={refreshKey}
                onRefresh={handleRefresh}
                onOverviewChange={handleOverviewChange}
              />
            </div>
          )}
          {activeTab === "activity" && (
            <div className="flex-1 overflow-hidden mt-0 min-h-0">
              <AgenticActivity global />
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
}
